/*
 * Authors: mariushirjanu
 * Created: 27/03/2023
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { util } from '../../../../shared/util';
import { IContact, ICountry, ILocationExtended, INamedObject, IRecord, IState } from '../../../../../type_definitions/datatypes';
import { HttpDataService, IHttpDataCache } from '../../../../shared/http/http-data.service';
import { LOV_COUNTRY, LOV_STATE, LovService } from '../../../../shared/services/lov.service';

export enum OrderEntryContactType {
  MY_CONTACT = 'MY_CONTACT',
  DEFAULT_CONTACT = 'DEFAULT_CONTACT',
  MANUAL_CONTACT = 'MANUAL_CONTACT',
  CURRENT_CONTACT = 'CURRENT_CONTACT'
}

export enum OrderEntryContactTypeExtra {
  RECENT_INVOICE_CONTACTS = 'RECENT_INVOICE_CONTACTS',
  SAME_AS_SHIPPING_CONTACT = 'SAME_AS_SHIPPING_CONTACT',
  RECENT_SHIP_CONTACTS = 'RECENT_SHIP_CONTACTS'
}

export type InvoiceContactType = OrderEntryContactType | OrderEntryContactTypeExtra.RECENT_INVOICE_CONTACTS | OrderEntryContactTypeExtra.SAME_AS_SHIPPING_CONTACT;
export type ShippingContactType = OrderEntryContactType | OrderEntryContactTypeExtra.RECENT_SHIP_CONTACTS;

export enum OrderEntryAddressType {
  EXISTING_LOCATION = 'EXISTING_LOCATION',
  MANUAL_ADDRESS = 'MANUAL_ADDRESS',
  RECENT_ADDRESS = 'RECENT_ORDERS_ADDRESS',
  CURRENT_ADDRESS = 'CURRENT_ADDRESS'
}

export enum OrderEntryAddressTypeExtra {
  SAME_AS_SHIPPING_ADDRESS = 'SAME_AS_SHIPPING_ADDRESS',
  DEFAULT_INVOICE_ADDRESS = 'DEFAULT_INVOICE_ADDRESS',
  DEFAULT_SHIPPING_ADDRESS = 'DEFAULT_ADDRESS',
  OFFICE_ADDRESS = 'OFFICE_ADDRESS'
}

export type InvoiceAddressType = OrderEntryAddressType | OrderEntryAddressTypeExtra.SAME_AS_SHIPPING_ADDRESS | OrderEntryAddressTypeExtra.DEFAULT_INVOICE_ADDRESS;
export type ShippingAddressType = OrderEntryAddressType | OrderEntryAddressTypeExtra.DEFAULT_SHIPPING_ADDRESS | OrderEntryAddressTypeExtra.OFFICE_ADDRESS;

export enum LocationDataType {
  SHIPPING = 'SHIPPING',
  INVOICE = 'INVOICE'
}

export enum LocationProperty {
  SHIP_TO = 'shipto',
  INVOICE_TO = 'invoiceto'
}

export interface IOrderEntryContact {
  type:InvoiceContactType | ShippingContactType;
  description:string;
  contact:IContact;
}

export interface IOrderEntryAddress extends IRecord {
  description:string;
  location:ILocationExtended;
  contact:IContact;
  comment:string;
  vat:IVat;
  default:boolean;
  editable:boolean;
}

export interface IInvoiceAddress extends IOrderEntryAddress {
  type:InvoiceAddressType;
  invoiceToContact:InvoiceContactType;
  invoiceEmailAddress:string;
}

export interface IShippingAddress extends IOrderEntryAddress {
  type:ShippingAddressType;
  office:string;
  shipToContact:ShippingContactType;
}

export interface ILocationOption extends IOrderEntryAddress {
  invoiceToContact?:InvoiceContactType;
}

export interface IInvoiceLocation {
  contact?:IContact;
  location:ILocationExtended & IOrderEntryAddress;
  externalValue?:IExternalValue;
}

export interface IExternalValue {
  externalId:IExternalValueContext;
  externalValue:string;
  id:number;
}

export interface IExternalValueContext {
  externalCode:string;
  externalValueRegex?:string;
  id:number;
  gaIdAsExternalValue?:boolean;
}

export interface ILocationSubCompany extends INamedObject {
  internalId:string;
  comments:string;
}

export interface IVat {
  isMandatory:boolean;
  name:string;
  regex:string;
  format:string;
}

const CANCELID_LOCATIONS:string = 'loadLocations';
const CANCELID_TAX:string = 'loadTaxInfo';

@Injectable()
export class OrderEntryLocationDataService {
  public LOC_TYPES:(InvoiceAddressType | ShippingAddressType)[] = [
    OrderEntryAddressType.CURRENT_ADDRESS,
    OrderEntryAddressTypeExtra.DEFAULT_SHIPPING_ADDRESS,
    OrderEntryAddressTypeExtra.DEFAULT_INVOICE_ADDRESS,
    OrderEntryAddressType.EXISTING_LOCATION,
    OrderEntryAddressTypeExtra.OFFICE_ADDRESS,
    OrderEntryAddressType.MANUAL_ADDRESS,
    OrderEntryAddressType.RECENT_ADDRESS,
    OrderEntryAddressTypeExtra.SAME_AS_SHIPPING_ADDRESS
  ];

  public CONTACT_TYPES:(InvoiceContactType | ShippingContactType)[] = [
    OrderEntryContactType.CURRENT_CONTACT,
    OrderEntryContactType.MY_CONTACT,
    OrderEntryContactType.DEFAULT_CONTACT,
    OrderEntryContactTypeExtra.RECENT_SHIP_CONTACTS,
    OrderEntryContactTypeExtra.RECENT_INVOICE_CONTACTS,
    OrderEntryContactType.MANUAL_CONTACT,
    OrderEntryContactTypeExtra.SAME_AS_SHIPPING_CONTACT
  ];

  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService,
    private lovService:LovService
  ) {
    this.cache = httpDataService.createCache();
  }

  public addresses = (dataType:LocationDataType, requesterId:number, selectedShopId:number, selectedCountryId:number, testMode:boolean, bpId:number, billingType?:string):Promise<IInvoiceAddress[] | IShippingAddress[]> => {
    this.checkDataType(dataType);

    let params = {
      test: testMode,
      requesterId: requesterId
    };

    if (dataType === LocationDataType.INVOICE) {
      params = _.extend(params, {
        companyCountryId: bpId
      });
      util.setValue(params, 'billingType', billingType, true);
    }

    return this.cache.doGET(
      `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/${this.locationProperty(dataType)}addresses`,
      params,
      undefined,
      'Error loading locations'
    );
  };

  public contacts = (dataType:LocationDataType, requesterId:number, selectedShopId:number, selectedCountryId:number, billingType?:string):Promise<IOrderEntryContact[]> => {
    this.checkDataType(dataType);

    const params = { requesterId };
    if (dataType === LocationDataType.INVOICE) {
      util.setValue(params, 'billingType', billingType, true);
    }

    return this.cache.doGET(
      `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/${this.locationProperty(dataType)}contacts`,
      params,
      undefined,
      'Error loading contacts'
    );
  };

  public countries = (dataType:LocationDataType, requesterId:number, selectedShopId:number, selectedCountryId:number):Promise<ICountry[]> => {
    this.checkDataType(dataType);

    let url:string;
    if (dataType === LocationDataType.SHIPPING) {
      url = `rest/orderentry/webshops/${selectedShopId}/shiptocountries`;
    } else {
      url = `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/invoicetocountries`;
    }
    return this.cache.doGET(
      url,
      { requesterId },
      undefined,
      'Error loading countries'
    );
  };

  public allCountries = ():Promise<ICountry[]> => {
    return this.lovService.list(LOV_COUNTRY);
  };

  public states = (countryId:number):Promise<IState[]> => {
    return this.lovService.list(`${LOV_STATE}-${countryId}`);
  };

  public locations = (dataType:LocationDataType, countryId:number, stateId:number | string, requesterId:number, selectedShopId:number, selectedCountryId:number, billingType:string, selectedBpId?:number):Promise<IOrderEntryAddress[]> => {
    this.checkDataType(dataType);

    const url:string = `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/${this.locationProperty(dataType)}locations`;
    let params;
    if (dataType === LocationDataType.SHIPPING) {
      params = {
        stateId: stateId,
        requesterId: requesterId,
        shiptoCountryId: countryId
      };
    } else {
      params = {
        invoiceCountryId: countryId,
        billingType: billingType,
        stateId: stateId,
        bpId: selectedBpId,
        requesterId
      };
    }

    return this.cache.doGET(
      url,
      params,
      undefined,
      'Error loading locations',
      CANCELID_LOCATIONS
    );
  };

  public recentLocations = (dataType:LocationDataType, selectedShopId:number, selectedCountryId:number, testMode:boolean):Promise<IOrderEntryAddress[]> => {
    this.checkDataType(dataType);

    return this.cache.doGET(
      `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/${this.locationProperty(dataType)}recentaddresses`,
      { test: testMode },
      undefined,
      'Error loading locations'
    );
  };

  public taxInfo = (dataType:LocationDataType, taxCountryId:number, selectedShopId:number, selectedCountryId:number):Promise<IVat> => {
    this.checkDataType(dataType);
    const url:string = `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/${dataType === LocationDataType.INVOICE? 'invoicingtaxinfo' : 'shippingtaxinfo'}`;

    return this.cache.doGET(
      url,
      { taxCountryId },
      undefined,
      'Error loading tax info',
      CANCELID_TAX
    );
  };

  public recentContacts = (dataType:LocationDataType, selectedShopId:number, selectedCountryId:number, testMode:boolean):Promise<IContact[]> => {
    this.checkDataType(dataType);

    return this.cache.doGET(
      `rest/orderentry/webshops/${selectedShopId}/countries/${selectedCountryId}/${this.locationProperty(dataType)}recentcontacts`,
      { test: testMode },
      undefined,
      'Error loading contacts'
    );
  };

  public loading():boolean {
    return this.cache.loading();
  }

  public isLoadingDone():Promise<boolean> {
    return this.cache.isLoadingDone();
  }

  public addLocation(webshopId:number, countryId:number, location:ILocationExtended):Promise<ILocationExtended> {
    return this.httpDataService.doPOST(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/locations`,
      undefined,
      location,
      undefined,
      'Error saving location');
  }

  public loadLocation(webshopId:number, countryId:number, locationId:number):Promise<ILocationExtended> {
    return this.httpDataService.doGET(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/locations/${locationId}`,
      undefined,
      undefined,
      undefined,
      `Error loading location ${locationId}`
    );
  }

  public updateLocation(webshopId:number, countryId:number, locationId:number, location:ILocationExtended):Promise<ILocationExtended> {
    return this.httpDataService.doPATCH(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/locations/${locationId}`,
      undefined,
      location,
      undefined,
      `Error updating location ${locationId}`
    );
  }

  public loadSubCompanies(webshopId:number, countryId:number):Promise<ILocationSubCompany[]> {
    return this.httpDataService.doGET(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/customersubcompanies`,
      undefined,
      undefined,
      'Error loading subcompanies'
    );
  }

  public locationProperty(dataType:LocationDataType):LocationProperty {
    return dataType === LocationDataType.SHIPPING ? LocationProperty.SHIP_TO : LocationProperty.INVOICE_TO;
  }

  private checkDataType(dataType:LocationDataType):void {
    if (!dataType || !_.contains([
      LocationDataType.SHIPPING,
      LocationDataType.INVOICE
    ], dataType)) {
      throw new Error(`provided dataType is invalid: ${dataType}`);
    }
  }

  public clear():void {
    this.cache.clear();
  }
}